package components

import androidx.compose.runtime.*
import dev.kilua.compose.ComponentNode
import dev.kilua.core.IComponent
import dev.kilua.html.*
import i18n.t
import models.GameState
import models.isAdjacentSector
import models.isCharacterVisibleToPlayer
import models.GameAction
import models.MapData
import models.Team
import models.ScrollType
import models.estimateWinChance
import models.BattleStrategy
import models.canUseStrategy
import models.strategyBonus

data class PendingBattle(
    val targetSector: String,
    val characterId: String,
    val isPlacement: Boolean
)

/** Map image native size; all territory coordinates are expressed in this space. */
private const val MAP_WIDTH = 1205
private const val MAP_HEIGHT = 1095

/** All unique undirected connections between territories (computed once from MapData). */
private val MapConnections: List<Pair<String, String>> by lazy {
    val seen = mutableSetOf<String>()
    val edges = mutableListOf<Pair<String, String>>()
    for ((id, territory) in MapData) {
        for (adj in territory.adjacentIds) {
            if (!MapData.containsKey(adj)) continue
            val (a, b) = if (id < adj) id to adj else adj to id
            if (seen.add("$a-$b")) edges.add(a to b)
        }
    }
    edges
}

/** Builds the SVG markup drawing every connection; edges touching [focusSector] are highlighted. */
private fun buildConnectionsSvg(focusSector: String?): String {
    val sb = StringBuilder()
    sb.append("<svg class=\"map-connections-svg\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 $MAP_WIDTH $MAP_HEIGHT\" preserveAspectRatio=\"none\">")
    val normal = StringBuilder()
    val focused = StringBuilder()
    for ((a, b) in MapConnections) {
        val ta = MapData[a] ?: continue
        val tb = MapData[b] ?: continue
        val isFocus = focusSector != null && (a == focusSector || b == focusSector)
        val target = if (isFocus) focused else normal
        val cls = if (isFocus) "map-path map-path-focus" else "map-path"
        val coords = "x1=\"${ta.x}\" y1=\"${ta.y}\" x2=\"${tb.x}\" y2=\"${tb.y}\""
        target.append("<line class=\"map-path-glow${if (isFocus) " map-path-glow-focus" else ""}\" $coords vector-effect=\"non-scaling-stroke\"/>")
        target.append("<line class=\"$cls\" $coords vector-effect=\"non-scaling-stroke\"/>")
    }
    // Focused edges are drawn last so they sit on top of the regular network
    sb.append(normal).append(focused)
    sb.append("</svg>")
    return sb.toString()
}

@Composable
fun IComponent.StrategicMap(
    playerId: String,
    gameState: GameState?,
    selectedCharacterId: String?,
    onSelectCharacter: (String) -> Unit,
    activeFight: models.GameEvent.FightOccurred? = null,
    activeNatureEvents: List<models.GameEvent.NatureEventOccurred> = emptyList(),
    activeTransfers: List<models.GameEvent.ResourceTransferred> = emptyList(),
    activeTavernDialogue: models.GameEvent.TavernDialogueReceived? = null,
    activeHunterIntel: models.GameEvent.HunterIntelReceived? = null,
    onDismissTavernDialogue: () -> Unit = {},
    onDismissHunterIntel: () -> Unit = {},
    sendAction: (GameAction) -> Unit
) {
    var openedTerritoryId by remember { mutableStateOf<String?>(null) }
    var pendingBattle by remember { mutableStateOf<PendingBattle?>(null) }
    var selectedStrategy by remember { mutableStateOf(BattleStrategy.NONE) }
    var showAllLabels by remember { mutableStateOf(false) }
    var showPaths by remember { mutableStateOf(false) }
    var hideLabels by remember { mutableStateOf(false) }
    
    val myPlayer = gameState?.players?.find { it.id == playerId }
    val myCharacters = gameState?.characters?.filter { it.playerId == playerId } ?: emptyList()
    val isMyTurn = gameState?.activeTeamTurn == myPlayer?.team
    
    val activeChar = myCharacters.find { it.id == selectedCharacterId }
        ?: myCharacters.find { !it.hasActedThisTurn && !it.isDead }
        ?: myCharacters.firstOrNull()

    div(className = "strategic-map-container") {
        div(className = if (hideLabels) "strategic-map-viewport map-clean-mode" else "strategic-map-viewport") {
            // Map visualisation toolbar
            div(className = "map-toolbar") {
                // Connections / paths toggle
                button(
                    if (showPaths) t("map.paths_hide") else t("map.paths_show"),
                    className = if (showPaths) "map-toolbar-btn map-toolbar-btn-active" else "map-toolbar-btn"
                ) {
                    id("map-toggle-paths-btn")
                    title(t("map.paths_tip"))
                    onClick {
                        showPaths = !showPaths
                    }
                }
                // Hide / show all labels (clean map view)
                button(
                    if (hideLabels) t("map.labels_show") else t("map.labels_hide"),
                    className = if (hideLabels) "map-toolbar-btn map-toolbar-btn-active" else "map-toolbar-btn"
                ) {
                    id("map-toggle-labels-btn")
                    title(t("map.labels_tip"))
                    onClick {
                        hideLabels = !hideLabels
                    }
                }
                // Map Labels density toggle (only meaningful while labels are visible)
                if (!hideLabels) {
                    button(
                        if (showAllLabels) t("map.names_all") else t("map.names_key"),
                        className = "map-toolbar-btn map-labels-toggle-btn"
                    ) {
                        id("map-toggle-names-btn")
                        onClick {
                            showAllLabels = !showAllLabels
                        }
                    }
                }
            }

            // The background map image
            img(src = "/gothic_fire_map.png", alt = "Gothic Strategic Map", className = "strategic-map-image")

            // Connections layer: every path between locations, drawn beneath the territory nodes
            if (showPaths) {
                val focusSector = openedTerritoryId ?: activeChar?.currentSector
                div(className = "map-connections-layer") {
                    rawHtmlBlock(buildConnectionsSvg(focusSector))
                }
                div(className = "map-paths-legend") {
                    div(className = "map-paths-legend-row") {
                        span(className = "map-paths-legend-swatch") {}
                        span { textNode(t("map.legend_path", MapConnections.size)) }
                    }
                    div(className = "map-paths-legend-row") {
                        span(className = "map-paths-legend-swatch map-paths-legend-swatch-focus") {}
                        span { textNode(t("map.legend_focus")) }
                    }
                }
            }

            // The Territory Nodes and Unit Markers Overlay (unified coordinate system)
            div(className = "strategic-map-overlay strategic-map-grid") {
                for ((sector, territory) in MapData) {
                    val terrState = gameState?.territories?.get(sector)
                    val terrOwnerTeam = terrState?.ownerTeam ?: terrState?.ownerPlayerId?.let { opId -> gameState?.players?.find { it.id == opId }?.team }
                    
                    val sectorOccupiedBy = gameState?.characters?.find { it.currentSector == sector && !it.isDead }
                    val isSectorSelected = activeChar != null && activeChar.currentSector == sector
                    
                    val occupantPlayer = sectorOccupiedBy?.let { occ -> gameState?.players?.find { it.id == occ.playerId } }
                    val isEnemySector = occupantPlayer != null && occupantPlayer.team != myPlayer?.team
                    
                    // Valid move target if active character is placed, alive, hasn't acted, it's my turn, and target is adjacent and (empty or enemy)
                    val isValidMoveTarget = isMyTurn && activeChar != null && !activeChar.hasActedThisTurn && !activeChar.isDead &&
                        activeChar.currentSector != null && (sectorOccupiedBy == null || isEnemySector) &&
                        isAdjacentSector(activeChar.currentSector, sector)
                    
                    // Valid placement target if active character is unplaced, alive, hasn't acted, it's my turn, and target is empty or enemy
                    val isValidPlacementTarget = isMyTurn && activeChar != null && !activeChar.hasActedThisTurn && !activeChar.isDead &&
                        activeChar.currentSector == null && (sectorOccupiedBy == null || isEnemySector)

                    val leftPerc = (territory.x / 1205.0) * 100
                    val topPerc = (territory.y / 1095.0) * 100

                    val curProt = terrState?.protection ?: territory.protection
                    val curCult = terrState?.cultivation ?: territory.cultivation

                    val cellClasses = mutableListOf("territory-node")
                    if (isSectorSelected) cellClasses.add("territory-node-selected")
                    if (isValidMoveTarget) cellClasses.add("territory-node-valid-move")
                    if (isValidPlacementTarget) cellClasses.add("territory-node-valid-place")
                    if (activeFight?.sectorId == sector) {
                        cellClasses.add("territory-node-fight")
                        when (activeFight?.strategy) {
                            BattleStrategy.ARCANE_PHALANX -> cellClasses.add("fight-strategy-phalanx")
                            BattleStrategy.HAMMER_AND_SPELL -> cellClasses.add("fight-strategy-hammer")
                            BattleStrategy.SPELL_INFUSED_VOLLEY -> cellClasses.add("fight-strategy-volley")
                            else -> {}
                        }
                    }
                    
                    if (curProt >= 16) {
                        cellClasses.add("territory-node-high-protection")
                    }
                    if (curCult >= 16) {
                        cellClasses.add("territory-node-high-cultivation")
                    }

                    div(className = cellClasses.joinToString(" ")) {
                        title("${territory.name ?: "Sector $sector"} (#$sector) | 🛡️ Prot: $curProt | 🌾 Cult: $curCult (+${(curCult / 2).coerceAtLeast(1)} Food/turn)")
                        style("left", "$leftPerc%")
                        style("top", "$topPerc%")
                        
                        if (terrOwnerTeam != null) {
                            val teamInfo = gameState?.teamInfos?.get(terrOwnerTeam)
                            if (teamInfo != null) {
                                style("border", "2px solid ${teamInfo.color} !important")
                                style("backgroundColor", "${teamInfo.color}66 !important")
                                style("boxShadow", "0 0 10px ${teamInfo.color}b3")
                            }
                        }
                        style("position", "absolute")
                        style("transform", "translate(-50%, -50%)")
                        
                        if (territory.isCastle) {
                            img(src = "/Castle_icon.png?v=2", alt = "Castle", className = "territory-castle-icon")
                        } else if (!hideLabels) {
                            span(className = "territory-label") {
                                textNode(sector)
                            }
                        }

                        val shouldShowLabel = territory.name != null && (
                            showAllLabels ||
                            territory.isCastle ||
                            isSectorSelected ||
                            openedTerritoryId == sector ||
                            sectorOccupiedBy != null ||
                            territory.protection >= 16 ||
                            territory.cultivation >= 17 ||
                            territory.adjacentIds.size >= 6
                        )
                        if (territory.name != null && !hideLabels) {
                            val labelClass = if (shouldShowLabel) "territory-name-label" else "territory-name-label territory-name-label-hover"
                            div(className = labelClass) {
                                textNode(territory.name)
                            }
                        }
                        
                        // Active nature events are now handled as modals in App.kt
                        
                        // Render resource transfers for this sector
                        val transferOut = activeTransfers.find { it.fromSectorId == sector }
                        if (transferOut != null) {
                            val parts = mutableListOf<String>()
                            if (transferOut.food > 0) parts.add("-${transferOut.food}🌾")
                            if (transferOut.gold > 0) parts.add("-${transferOut.gold}💰")
                            div(className = "nature-event-toast nature-event-negative") {
                                textNode(parts.joinToString(" "))
                            }
                        }
                        val transferIn = activeTransfers.find { it.toSectorId == sector && it.fromSectorId != sector }
                        if (transferIn != null) {
                            val parts = mutableListOf<String>()
                            if (transferIn.food > 0) parts.add("+${transferIn.food}🌾")
                            if (transferIn.gold > 0) parts.add("+${transferIn.gold}💰")
                            div(className = "nature-event-toast nature-event-positive") {
                                textNode(parts.joinToString(" "))
                            }
                        }
                        
                        if (!hideLabels && terrState != null && (terrState.food > 0 || terrState.gold > 0)) {
                            div(className = "territory-res-pill") {
                                if (terrState.food > 0) span(className = "res-tag") { textNode("🌾${terrState.food}") }
                                if (terrState.gold > 0) span(className = "res-tag") { textNode("🪙${terrState.gold}") }
                            }
                        }

                        val sectorCamps = gameState?.huntingCamps?.filter { it.sectorId == sector } ?: emptyList()
                        if (sectorCamps.isNotEmpty()) {
                            val camp = sectorCamps.first()
                            val teamColor = gameState?.teamInfos?.get(camp.ownerTeam)?.color ?: "#FFD700"
                            div(className = "territory-camp-marker") {
                                title("⛺ Hunting Camp (${camp.ownerTeam.name})")
                                style("borderColor", teamColor)
                                textNode("⛺")
                            }
                        }
                        
                        if (activeFight?.sectorId == sector) {
                            val fightStrategy = activeFight?.strategy ?: BattleStrategy.NONE
                            val (fightIcon, overlayClass) = when (fightStrategy) {
                                BattleStrategy.ARCANE_PHALANX -> "🛡️" to "fight-overlay fight-overlay-phalanx"
                                BattleStrategy.HAMMER_AND_SPELL -> "⚔️" to "fight-overlay fight-overlay-hammer"
                                BattleStrategy.SPELL_INFUSED_VOLLEY -> "🔥" to "fight-overlay fight-overlay-volley"
                                else -> "⚔️" to "fight-overlay"
                            }
                            div(className = overlayClass) {
                                textNode(fightIcon)
                            }
                            if (fightStrategy != BattleStrategy.NONE) {
                                val stratLabel = when (fightStrategy) {
                                    BattleStrategy.ARCANE_PHALANX -> t("battle.strat_phalanx")
                                    BattleStrategy.HAMMER_AND_SPELL -> t("battle.strat_hammer")
                                    BattleStrategy.SPELL_INFUSED_VOLLEY -> t("battle.strat_volley")
                                    else -> ""
                                }
                                div(className = "fight-strategy-label fight-strategy-label-${fightStrategy.name.lowercase()}") {
                                    textNode(stratLabel)
                                }
                            }
                        }


                        // Handle clicks
                        onClick {
                            if (gameState == null || myPlayer == null) return@onClick
                            
                            // Priority 1: Unplaced active character places onto board
                            if (isValidPlacementTarget && activeChar != null) {
                                if (isEnemySector) {
                                    pendingBattle = PendingBattle(sector, activeChar.id, isPlacement = true)
                                } else {
                                    sendAction(GameAction.PlaceCharacter(sector, activeChar.id))
                                }
                                return@onClick
                            }
                            
                            // Priority 2: Move active character to valid adjacent target
                            if (isValidMoveTarget && activeChar != null) {
                                if (isEnemySector) {
                                    pendingBattle = PendingBattle(sector, activeChar.id, isPlacement = false)
                                } else {
                                    sendAction(GameAction.MoveCharacter(sector, activeChar.id))
                                }
                                return@onClick
                            }
                            
                            // Priority 3: Click on my own character's sector
                            val myOccChar = myCharacters.find { it.currentSector == sector && !it.isDead }
                            if (myOccChar != null) {
                                onSelectCharacter(myOccChar.id)
                                openedTerritoryId = sector
                                return@onClick
                            }
                            
                            // Priority 4: Open territory inspection modal
                            openedTerritoryId = sector
                        }
                    }
                }

                // Render Unit Markers inside the same overlay for locked alignment
                if (gameState != null) {
                    for (char in gameState.characters) {
                        if (char.isDead) continue
                        if (!isCharacterVisibleToPlayer(char, playerId, gameState)) continue
                        val sector = char.currentSector ?: continue
                        val territory = MapData[sector]
                        if (territory != null) {
                            val leftPerc = (territory.x / 1205.0) * 100
                            val topPerc = (territory.y / 1095.0) * 100
                            
                            val owner = gameState.players.find { it.id == char.playerId }
                            val isRed = owner?.team == Team.RED
                            val isMine = char.playerId == playerId
                            val isSelected = activeChar?.id == char.id
                            
                            val markerClass = if (isRed) "unit-marker-p1" else "unit-marker-p2"
                            val selectedClass = if (isSelected) "unit-marker-selected" else ""
                            val actedClass = if (char.hasActedThisTurn) "unit-marker-acted" else ""

                            div(className = "unit-marker $markerClass $selectedClass $actedClass") {
                                style("left", "$leftPerc%")
                                style("top", "$topPerc%")
                                title("${char.name} [${owner?.name ?: "Unknown"}] (WAR:${char.warlord} INT:${char.intellect} VAN:${char.vanguard} ARC:${char.archon} | Army: ⚔️${char.army.total()} | Bag: 🌾${char.food} 🪙${char.gold})${if (char.hasActedThisTurn) " [ACTED]" else ""}")
                                img(src = "/knight_icon.png?v=4", alt = "Knight", className = "unit-marker-img")
                                if (char.army.total() > 0) {
                                    span(className = "unit-marker-army-badge") {
                                        textNode("${char.army.total()}")
                                    }
                                }
                                
                                onClick {
                                    if (isMine) {
                                        onSelectCharacter(char.id)
                                    }
                                    openedTerritoryId = sector
                                }
                            }
                        }
                    }
                }
            }

            // Gothic Waiting Indicators:
            val isAiTurn = models.GothicIndicators.isComputerTurn(gameState)
            val aiTurnCount = gameState?.currentTurn ?: 1

            // Feature 1: The Swamp Camp "Swampweed Exhale" (Smoke Effect)
            SwampweedSmokeEffect(
                isThinking = isAiTurn,
                boardCorner = "bottom-right"
            )

            // Feature 2: Colony Chat Log ("Small Talk" Overlay) on the board corner
            if (isAiTurn && aiTurnCount > 0 && aiTurnCount % 3 == 0) {
                div(className = "board-corner-colony-chat") {
                    ColonyChatLog(
                        isThinking = isAiTurn,
                        aiTurnCount = aiTurnCount,
                        arrowPosition = "top-center"
                    )
                }
            }
        }
        
        // Render TerritoryCard modal if a territory is inspected
        if (openedTerritoryId != null) {
            TerritoryCard(
                sectorId = openedTerritoryId!!,
                playerId = playerId,
                gameState = gameState,
                selectedCharacterId = activeChar?.id,
                activeTavernDialogue = activeTavernDialogue,
                activeHunterIntel = activeHunterIntel,
                onDismissTavernDialogue = onDismissTavernDialogue,
                onDismissHunterIntel = onDismissHunterIntel,
                onClose = { openedTerritoryId = null },
                sendAction = sendAction
            )
        }
        
        // Pre-Battle Estimation & Preparation Popup
        if (pendingBattle != null && gameState != null) {
            val pb = pendingBattle!!
            val battleChar = myCharacters.find { it.id == pb.characterId }
            val enemyChar = gameState.characters.find { it.currentSector == pb.targetSector && !it.isDead }
            val enemyPlayer = gameState.players.find { it.id == enemyChar?.playerId }
            val territoryData = MapData[pb.targetSector]
            val terrState = gameState.territories[pb.targetSector]
            val locationProtection = terrState?.protection ?: (territoryData?.protection ?: 0)
            
            if (battleChar != null && enemyChar != null) {
                val winChance = estimateWinChance(battleChar, enemyChar, locationProtection, selectedStrategy, battleChar.siegeWeapons)
                val chanceClass = when {
                    winChance >= 70 -> "chance-high"
                    winChance >= 40 -> "chance-med"
                    else -> "chance-low"
                }
                
                val isAttacker10x = battleChar.army.total() >= 4 && (enemyChar.army.total() == 0 || battleChar.army.total() >= enemyChar.army.total() * 4)
                val isDefender10x = enemyChar.army.total() >= 4 && (battleChar.army.total() == 0 || enemyChar.army.total() >= battleChar.army.total() * 4)
                
                div(className = "territory-modal-backdrop") {
                    onClick {
                        pendingBattle = null
                        selectedStrategy = BattleStrategy.NONE
                    }
                    
                    div(className = "territory-modal-card glass battle-prep-modal") {
                        onClick { it.stopPropagation() }
                        
                        // Header
                        div(className = "d-flex justify-between items-center mb-1") {
                            h3(className = "m-0") { 
                                textNode(t("battle.prep_title", pb.targetSector))
                                if (territoryData?.name != null) textNode(" (${territoryData.name})")
                            }
                            button("✕", className = "btn-modal-close") {
                                onClick {
                                    pendingBattle = null
                                    selectedStrategy = BattleStrategy.NONE
                                }
                            }
                        }
                        
                        // Win Chance Banner
                        div(className = "battle-win-chance-banner $chanceClass mb-1") {
                            div(className = "d-flex justify-between items-center") {
                                span(className = "font-600 text-sm") { textNode(t("battle.win_prob")) }
                                span(className = "win-chance-value") { textNode("$winChance%") }
                            }
                            if (isAttacker10x) {
                                div(className = "army-domination-tag mt-03 text-xs") {
                                    textNode(t("battle.domination_attacker"))
                                }
                            } else if (isDefender10x) {
                                div(className = "army-domination-tag-danger mt-03 text-xs") {
                                    textNode(t("battle.domination_defender"))
                                }
                            } else {
                                val casualtyEstimate = when {
                                    winChance >= 75 -> t("battle.cas_light")
                                    winChance >= 45 -> t("battle.cas_med")
                                    else -> t("battle.cas_heavy")
                                }
                                div(className = "mt-03 text-xs text-gray") {
                                    textNode(casualtyEstimate)
                                }
                            }
                        }
                        
                        // Combatant Comparison Cards
                        div(className = "battle-comparison-grid mb-1") {
                            // Attacker Box
                            div(className = "combatant-card attacker-card glass") {
                                div(className = "combatant-header") {
                                    span(className = "font-600 text-primary") { textNode(battleChar.name) }
                                    span(className = "text-xs text-gray") { textNode(t("battle.attacker_you")) }
                                }
                                div(className = "combatant-stats mt-05") {
                                    div(className = "stat-row") {
                                        span { textNode(t("battle.stat_str")) }
                                        span { textNode("${battleChar.warlord}") }
                                    }
                                    div(className = "stat-row") {
                                        span { textNode(t("battle.stat_agi")) }
                                        span { textNode("${battleChar.vanguard}") }
                                    }
                                    div(className = "stat-row") {
                                        span { textNode(t("battle.stat_wis")) }
                                        span { textNode("${battleChar.intellect}") }
                                    }
                                    div(className = "stat-row font-600 text-warning") {
                                        span { textNode(t("battle.stat_army")) }
                                        span { textNode("${battleChar.army.total()}") }
                                    }
                                }
                            }
                            
                            // Defender Box
                            div(className = "combatant-card defender-card glass") {
                                div(className = "combatant-header") {
                                    val enemyColor = enemyPlayer?.team?.let { gameState?.teamInfos?.get(it)?.color } ?: "#ff4444"
                                    span(className = "font-600") { 
                                        style("color", enemyColor)
                                        textNode(enemyChar.name) 
                                    }
                                    span(className = "text-xs text-gray") { textNode(enemyPlayer?.name ?: t("hud.enemy_team")) }
                                }
                                div(className = "combatant-stats mt-05") {
                                    div(className = "stat-row") {
                                        span { textNode(t("battle.stat_str")) }
                                        span { textNode("${enemyChar.warlord}") }
                                    }
                                    div(className = "stat-row") {
                                        span { textNode(t("battle.stat_agi")) }
                                        span { textNode("${enemyChar.vanguard}") }
                                    }
                                    div(className = "stat-row") {
                                        span { textNode(t("battle.stat_wis")) }
                                        span { textNode("${enemyChar.intellect}") }
                                    }
                                    div(className = "stat-row font-600 text-warning") {
                                        span { textNode(t("battle.stat_army")) }
                                        span { textNode("${enemyChar.army.total()}") }
                                    }
                                    if (locationProtection > 0) {
                                        div(className = "stat-row text-xs text-gray") {
                                            span { textNode(t("battle.stat_prot")) }
                                            span { textNode("+$locationProtection") }
                                        }
                                    }
                                }
                            }
                        }
                        
                        // ====== BATTLE STRATEGY PICKER ======
                        div(className = "battle-strategy-section mb-1") {
                            div(className = "d-flex justify-between items-center mb-05") {
                                span(className = "font-600 text-sm") { textNode(t("battle.strategy_title")) }
                                if (selectedStrategy != BattleStrategy.NONE) {
                                    val bonus = strategyBonus(battleChar, selectedStrategy)
                                    span(className = "text-xs text-primary font-600") {
                                        textNode(t("battle.bonus_text", bonus.asDynamic().toFixed(1)))
                                    }
                                } else {
                                    span(className = "text-xs text-dark-gray") { textNode(t("battle.select_strat_tip")) }
                                }
                            }
                            
                            // Strategy cards
                            div(className = "d-flex flex-col gap-04") {
                                // 1. Arcane Phalanx
                                val canPhalanx = canUseStrategy(battleChar, BattleStrategy.ARCANE_PHALANX)
                                val phalanxSelected = selectedStrategy == BattleStrategy.ARCANE_PHALANX
                                div(className = "strategy-card${if (phalanxSelected) " strategy-card-selected" else ""}${if (!canPhalanx) " strategy-card-disabled" else ""}") {
                                    onClick {
                                        if (canPhalanx) {
                                            selectedStrategy = if (phalanxSelected) BattleStrategy.NONE else BattleStrategy.ARCANE_PHALANX
                                        }
                                    }
                                    div(className = "d-flex justify-between items-center") {
                                        div {
                                            span(className = "font-600 text-sm") { textNode(t("battle.strat_phalanx")) }
                                            if (canPhalanx) {
                                                span(className = "text-xs text-primary ml-05") {
                                                    textNode("+${strategyBonus(battleChar, BattleStrategy.ARCANE_PHALANX).asDynamic().toFixed(1)}")
                                                }
                                            }
                                        }
                                        if (!canPhalanx) {
                                            span(className = "text-xs text-red") { textNode(t("battle.strat_phalanx_req")) }
                                        }
                                    }
                                    p(className = "text-xs text-gray m-0 mt-02") {
                                        textNode(t("battle.strat_phalanx_desc"))
                                    }
                                }
                                
                                // 2. Hammer and Spell
                                val canHammer = canUseStrategy(battleChar, BattleStrategy.HAMMER_AND_SPELL)
                                val hammerSelected = selectedStrategy == BattleStrategy.HAMMER_AND_SPELL
                                div(className = "strategy-card${if (hammerSelected) " strategy-card-selected" else ""}${if (!canHammer) " strategy-card-disabled" else ""}") {
                                    onClick {
                                        if (canHammer) {
                                            selectedStrategy = if (hammerSelected) BattleStrategy.NONE else BattleStrategy.HAMMER_AND_SPELL
                                        }
                                    }
                                    div(className = "d-flex justify-between items-center") {
                                        div {
                                            span(className = "font-600 text-sm") { textNode(t("battle.strat_hammer")) }
                                            if (canHammer) {
                                                span(className = "text-xs text-primary ml-05") {
                                                    textNode("+${strategyBonus(battleChar, BattleStrategy.HAMMER_AND_SPELL).asDynamic().toFixed(1)}")
                                                }
                                            }
                                        }
                                        if (!canHammer) {
                                            span(className = "text-xs text-red") { textNode(t("battle.strat_hammer_req")) }
                                        }
                                    }
                                    p(className = "text-xs text-gray m-0 mt-02") {
                                        textNode(t("battle.strat_hammer_desc"))
                                    }
                                }
                                
                                // 3. Spell-Infused Volley
                                val canVolley = canUseStrategy(battleChar, BattleStrategy.SPELL_INFUSED_VOLLEY)
                                val volleySelected = selectedStrategy == BattleStrategy.SPELL_INFUSED_VOLLEY
                                div(className = "strategy-card${if (volleySelected) " strategy-card-selected" else ""}${if (!canVolley) " strategy-card-disabled" else ""}") {
                                    onClick {
                                        if (canVolley) {
                                            selectedStrategy = if (volleySelected) BattleStrategy.NONE else BattleStrategy.SPELL_INFUSED_VOLLEY
                                        }
                                    }
                                    div(className = "d-flex justify-between items-center") {
                                        div {
                                            span(className = "font-600 text-sm") { textNode(t("battle.strat_volley")) }
                                            if (canVolley) {
                                                span(className = "text-xs text-primary ml-05") {
                                                    textNode("+${strategyBonus(battleChar, BattleStrategy.SPELL_INFUSED_VOLLEY).asDynamic().toFixed(1)}")
                                                }
                                            }
                                        }
                                        if (!canVolley) {
                                            span(className = "text-xs text-red") { textNode(t("battle.strat_volley_req")) }
                                        }
                                    }
                                    p(className = "text-xs text-gray m-0 mt-02") {
                                        textNode(t("battle.strat_volley_desc"))
                                    }
                                }
                            }
                        }
                        
                        // Scrolls Section
                        div(className = "battle-scrolls-section mb-1") {
                            div(className = "d-flex justify-between items-center mb-05") {
                                span(className = "font-600 text-xs text-gray") { textNode(t("battle.scrolls_avail")) }
                                span(className = "text-xs text-dark-gray") { textNode(t("battle.scrolls_in_bag", battleChar.scrolls.size)) }
                            }
                            
                            if (battleChar.scrolls.isNotEmpty()) {
                                div(className = "d-flex flex-col gap-03") {
                                    for (scroll in battleChar.scrolls) {
                                        val (icon, label) = when (scroll.type) {
                                            ScrollType.WARLORD -> "⚔️" to t("scroll.warlord")
                                            ScrollType.INTELLECT -> "🧠" to t("scroll.intellect")
                                            ScrollType.VANGUARD -> "🛡️" to t("scroll.vanguard")
                                            ScrollType.ARCHON -> "🔮" to t("scroll.archon")
                                        }
                                        div(className = "battle-scroll-row d-flex justify-between items-center") {
                                            span(className = "text-xs") { textNode(t("kingdom.scroll_item", icon, label, scroll.boostAmount)) }
                                            button(t("battle.scroll_apply", scroll.boostAmount), className = "btn btn-xs btn-outline scroll-apply-btn") {
                                                title(t("battle.scroll_apply_tip", battleChar.name, label, scroll.boostAmount))
                                                onClick {
                                                    sendAction(GameAction.UseScroll(scroll.id, battleChar.id))
                                                }
                                            }
                                        }
                                    }
                                }
                            } else {
                                p(className = "text-xs text-dark-gray m-0") {
                                    textNode(t("battle.no_scrolls"))
                                }
                            }
                        }
                        
                        // Action Buttons
                        div(className = "d-flex gap-1 mt-1") {
                            button(t("battle.retreat"), className = "btn btn-outline flex-1") {
                                title(t("battle.retreat_tip"))
                                onClick {
                                    pendingBattle = null
                                    selectedStrategy = BattleStrategy.NONE
                                }
                            }
                            button(t("battle.confirm_attack"), className = "btn btn-primary flex-1") {
                                title(t("battle.confirm_attack_tip", pb.targetSector))
                                onClick {
                                    if (pb.isPlacement) {
                                        sendAction(GameAction.PlaceCharacter(pb.targetSector, battleChar.id, selectedStrategy))
                                    } else {
                                        sendAction(GameAction.MoveCharacter(pb.targetSector, battleChar.id, selectedStrategy))
                                    }
                                    pendingBattle = null
                                    selectedStrategy = BattleStrategy.NONE
                                }
                            }
                        }
                    }
                }
            } else {
                pendingBattle = null
                selectedStrategy = BattleStrategy.NONE
            }
        }
    }
}

